import {
  ContactsV2Query,
  ContactsV2QueryVariables,
  Contact,
  SortEnum,
  useGetContactsCountQuery,
  ContactSourceTypeEnum,
} from '@/types/generated/graphql';
import { useCursorPagination } from '@shared/hooks/use-cursor-pagination';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { useState } from 'react';
import { useDeleteBlockContacts } from './hooks/use-delete-block-contact';
import { useExportContacts } from './hooks/use-export-contacts';
import { CURSOR_CONTACTS_LIST } from '@/graphql/cursor.graphql';
import { useSearchParams } from 'react-router-dom';
import { contact_filter_key } from './hooks/use-contact-advanced-filter';

type SearchByField = 'name' | 'email' | 'phoneNumber';

type UseContactsOptions = {
  query?: string;
  searchBy?: SearchByField | SearchByField[];
  sourceType?: ContactSourceTypeEnum[];
};

export const useContacts = (options?: UseContactsOptions) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchParams] = useSearchParams();
  const [nameSort, setNameSort] = useState<SortEnum | undefined>(SortEnum.ASC);

  const [params, setParams] = useEncryptedParams();
  const [searchBy, setSearchBy] = useState<SearchByField>('name');
  const paramsContactId = params.get('openContactId');
  const selectedContactId = paramsContactId === 'new' ? null : +(paramsContactId ?? 0);

  const countryCodeFilter = searchParams
    .getAll(contact_filter_key.country_code)
    .map((country) => country.split(',')[0]);
  const tagsFilter = searchParams.getAll(contact_filter_key.tags).map((tag) => +tag.split(',')[0]);

  const sourceTypeFilter = searchParams
    .getAll(contact_filter_key.source_types)
    .map((sourceType) => sourceType.split(',')[0]) as ContactSourceTypeEnum[];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const contactFilterVariables = {
    query: options?.query ?? params.get('query') ?? '',
    countryCode: countryCodeFilter,
    tags: tagsFilter,
    // by default exclude system from source
    sourceType:
      options?.sourceType ??
      (sourceTypeFilter.length > 0 ? sourceTypeFilter : [ContactSourceTypeEnum.imported, ContactSourceTypeEnum.manual]),
  };

  const searchByFields = options?.searchBy ?? searchBy;
  const selectedFields = Array.isArray(searchByFields) ? searchByFields : [searchByFields];

  const { data: contactCount } = useGetContactsCountQuery({
    variables: contactFilterVariables,
  });

  const { data, loading, initialLoading, loadNextRef, hasNextPage } = useCursorPagination<
    ContactsV2Query,
    ContactsV2QueryVariables
  >({
    schema: CURSOR_CONTACTS_LIST,
    variables: {
      order: 'name',
      sort: nameSort,
      fields: selectedFields,
      ...contactFilterVariables,
    },
    responseDataKey: 'contactsV2',
  });

  function setFilter(key: string, value: string) {
    params.set(key, value);
    setParams();
  }

  function setSearchByField(value: SearchByField) {
    setSearchBy(value);
  }

  function handleNameSort() {
    setNameSort((prev) => (prev === undefined ? SortEnum.ASC : prev === SortEnum.ASC ? SortEnum.DESC : undefined));
  }

  const deleteModal = useDeleteBlockContacts({ selectedRowKeys, setSelectedRowKeys });

  const { exportContacts, exportLoading } = useExportContacts();

  const handleRowClick = (contact: Contact) => {
    params.set('openContactId', contact?.id + '');
    setParams();
  };

  return {
    contactList: {
      data,
      loading,
      initialLoading,
      setFilter,
      loadNextRef,
      hasNextPage,
      rowSelection,
      handleRowClick,
      selectedContactId,
      // Open add drawer and ensure update drawer is closed
      openDrawer: !!paramsContactId,
      showDrawer: () => {
        params.set('openContactId', 'new');
        setParams();
      },
      onClose: () => {
        params.delete('openContactId');
        setParams();
      },
      ...deleteModal,
      contactCount,
      exportContacts: () =>
        exportContacts({
          selectedRowKeys,
          setSelectedRowKeys,
        }),
      exportLoading: exportLoading,
      searchBy,
      setSearchBy: setSearchByField,
      nameSort,
      handleNameSort,
    },
  };
};
